/**
 * HoursScreen.jsx
 * ---------------
 * Main screen for Education > Lectures > Lecture Hours & Faculty Ledger.
 * Complies strictly with module_implementation_standards.md:
 * - Real API integration (Rule 90)
 * - SharedFilters & AppPagination reuse
 * - PredefinedReportsDrawer & Print Engine
 * - JobEngine background exports (Rule 89)
 */
import React from 'react'
import SharedFilters from '../../../components/SharedFilters'
import AppPagination from '../../../components/AppPagination'
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer'
import HoursMetricsBar from './components/HoursMetricsBar'
import useHoursScreen from './hooks/useHoursScreen'

export default function HoursScreen() {
  const { state, actions } = useHoursScreen()

  const toolsConfig = [
    { key: 'reports-drawer', label: 'Predefined Reports', icon: 'description' },
    { key: 'export-excel', label: 'Export to Excel', icon: 'file_download' },
    { key: 'export-pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
  ]

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Header Canopy ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Education Operations</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Lecture Hours &amp; Faculty Ledger
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Reconcile faculty teaching hours, delivered lecture durations, and course curriculum progress.
          </p>
        </div>
      </section>

      {/* ── KPI Summary Metrics Bar ── */}
      <HoursMetricsBar metrics={state.metrics} loading={state.loading} />

      {/* ── Filters Bar (SharedFilters) ── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        courseFilter={state.courseFilter}
        schoolFilter={state.schoolFilter}
        facultyFilter={state.facultyFilter}
        academicYearFilter={state.academicYearFilter}
        periodFilter={state.periodFilter}
        batchFilter={state.batchFilter}
        subjectFilter={state.subjectFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setCourseFilter={actions.setCourseFilter}
        setSchoolFilter={actions.setSchoolFilter}
        setFacultyFilter={actions.setFacultyFilter}
        setAcademicYearFilter={actions.setAcademicYearFilter}
        setPeriodFilter={actions.setPeriodFilter}
        setBatchFilter={actions.setBatchFilter}
        setSubjectFilter={actions.setSubjectFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onReset={actions.handleReset}
        onSearch={() => actions.setCurrentPage(1)}
        searchFieldOptions={state.options.searchFields}
        companyOptions={state.options.companies}
        branchOptions={state.options.branches}
        courseOptions={state.options.courses}
        schoolOptions={state.options.schools}
        facultyOptions={state.options.faculty}
        batchOptions={state.options.batches}
        subjectOptions={state.options.subjects}
        academicYearOptions={state.options.academicYears}
        visibleAccordionFilters={[
          'company',
          'branch',
          'course',
          'school',
          'faculty',
          'academicYear',
          'period',
          'fromDate',
          'toDate',
          'batch',
          'subject',
        ]}
        tools={toolsConfig}
        onToolSelect={(key) => {
          if (key === 'reports-drawer') {
            actions.setIsReportsOpen(true)
          } else if (key === 'export-excel') {
            actions.handleExportExcel()
          } else if (key === 'export-pdf') {
            actions.handleExportPdf()
          }
        }}
      />

      {/* ── Hours Ledger Data Grid Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-xs text-on-surface">
            {/* Table Header */}
            <thead className="border-b border-outline-variant/25 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant select-none">
              <tr>
                <th
                  onClick={() => actions.handleSort('company')}
                  className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center gap-1">
                    <span>Company / Branch</span>
                    {state.sortBy === 'company' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => actions.handleSort('course')}
                  className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center gap-1">
                    <span>Course / Batch</span>
                    {state.sortBy === 'course' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => actions.handleSort('subject')}
                  className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center gap-1">
                    <span>Subject / Topic</span>
                    {state.sortBy === 'subject' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => actions.handleSort('date')}
                  className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center gap-1">
                    <span>Date &amp; Time</span>
                    {state.sortBy === 'date' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => actions.handleSort('faculty')}
                  className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center gap-1">
                    <span>Faculty &amp; School</span>
                    {state.sortBy === 'faculty' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => actions.handleSort('hours')}
                  className="px-5 py-3.5 text-right cursor-pointer hover:text-primary transition"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Duration (Hours)</span>
                    {state.sortBy === 'hours' && (
                      <span className="material-symbols-outlined text-xs">
                        {state.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-outline-variant/15 font-medium">
              {state.loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                        progress_activity
                      </span>
                      <span>Loading lecture hours ledger...</span>
                    </div>
                  </td>
                </tr>
              ) : state.records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">
                        timelapse
                      </span>
                      <p className="font-bold text-on-surface">No Lecture Sessions Found</p>
                      <p className="text-[11px]">Try adjusting your filter dates or selected course criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                state.records.map((item) => (
                  <tr
                    key={item.id}
                    className="group transition-colors hover:bg-surface-container-low/50"
                  >
                    {/* Company / Branch */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface group-hover:text-primary transition-colors">
                          {item.company || '—'}
                        </span>
                        <span className="inline-flex w-fit items-center rounded-md bg-surface-container-high px-2 py-0.5 text-[10px] font-bold text-on-surface-variant">
                          {item.branch || 'All Branches'}
                        </span>
                      </div>
                    </td>

                    {/* Course / Batch */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-primary">
                          {item.course || '—'}
                        </span>
                        <span className="text-[11px] font-medium text-on-surface-variant">
                          {item.batch || 'All Batches'}
                        </span>
                      </div>
                    </td>

                    {/* Subject / Topic */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface">
                          {item.subject || '—'}
                        </span>
                        <span className="text-[11px] text-on-surface-variant font-medium">
                          {item.chapterNo ? `Ch. ${item.chapterNo}: ` : ''}{item.chapterName || 'General Topic'}
                        </span>
                      </div>
                    </td>

                    {/* Date / Time */}
                    <td className="px-5 py-3.5 align-top whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 font-bold text-on-surface">
                          <span className="material-symbols-outlined text-xs text-outline-variant">
                            calendar_today
                          </span>
                          <span>{item.date || '—'}</span>
                        </div>
                        <span className="text-[11px] font-semibold text-on-surface-variant ml-4">
                          {item.startTime} - {item.endTime}
                        </span>
                      </div>
                    </td>

                    {/* Faculty / School-College */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                          {item.facultyAvatar || 'FC'}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="font-bold text-on-surface truncate">
                            {item.faculty || '—'}
                          </span>
                          <span className="text-[10px] text-on-surface-variant truncate">
                            {item.schoolCollege || 'Main Campus'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Duration / Hours */}
                    <td className="px-5 py-3.5 align-top text-right whitespace-nowrap">
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1 rounded-xl bg-emerald-500/10 px-2.5 py-1 text-xs font-extrabold text-emerald-700 border border-emerald-500/20">
                          <span className="material-symbols-outlined text-xs">schedule</span>
                          <span>{item.timeDiff || '0:00'}</span>
                        </span>
                        <span className="text-[10px] font-semibold text-on-surface-variant">
                          {item.timeMin || 0} mins
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Pagination ── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing Page <span className="font-bold text-on-surface">{state.currentPage}</span> of{' '}
            <span className="font-bold text-on-surface">{state.totalPages}</span>{' '}
            <span className="ml-1 text-primary font-bold">({state.metrics.hoursFormattedSummary})</span>
          </p>

          <AppPagination
            page={state.currentPage}
            totalPages={state.totalPages}
            onPageChange={actions.setCurrentPage}
            onPrevious={() => actions.setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setCurrentPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.currentPage > 1}
            canNext={state.currentPage < state.totalPages}
          />
        </div>
      </section>

      {/* ── Predefined Reports Drawer ── */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsOpen}
        onClose={() => actions.setIsReportsOpen(false)}
        title="Lecture Hours Reports"
        module="education"
        submodule="hours"
        filters={{
          academicYear: state.academicYearFilter,
          companyId: state.companyFilter,
          branchId: state.branchFilter,
          courseId: state.courseFilter,
          batchId: state.batchFilter,
          subjectId: state.subjectFilter,
          facultyId: state.facultyFilter,
          fromDate: state.fromDate,
          toDate: state.toDate,
          searchField: state.searchField,
          searchText: state.searchText,
        }}
      />
    </div>
  )
}
